// Turns a raw buyer-activity event payload into the human-readable chips the
// agent reads on a contact's timeline — specifically "what did they search" and
// "what did they set their preferences to" (Michael Piccoli, Monday 13090960800).
//
// Kept dependency-free (no React / axios) so it is trivially unit-testable and
// safe to reuse. ContactActivityTimeline renders the returned strings as badges.

// A preference/search value counts as "set" only if the buyer actually chose
// something — the portal stores the untouched default as 'Any' (and empty
// strings / empty arrays), which we never want to render as a real criterion.
export function isChosen(v) {
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0;
    const s = String(v).trim();
    return s !== '' && s.toLowerCase() !== 'any';
}

function currency(n) {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: 'AUD', maximumFractionDigits: 0,
    }).format(n);
}

// Values arrive as a raw number, a digits-only string ("2500000"), or an
// already-formatted string ("$1,300,000" / "500m²"). Show tidy currency for a
// plain number, otherwise pass through whatever the payload already had.
export function moneyLabel(v) {
    if (!isChosen(v)) return null;
    if (typeof v === 'number') return currency(v);
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return currency(Number(s));
    return s;
}

// "$X – $Y" / "$X+" / "Up to $Y" from a low/high pair, skipping unset ends.
export function rangeLabel(lo, hi, { plus = '+', upto = 'Up to ' } = {}) {
    const l = moneyLabel(lo);
    const h = moneyLabel(hi);
    if (l && h) return `${l} – ${h}`;
    if (l) return `${l}${plus}`;
    if (h) return `${upto}${h}`;
    return null;
}

export function countRange(lo, hi, unit) {
    const l = isChosen(lo) ? String(lo).trim() : null;
    const h = isChosen(hi) ? String(hi).trim() : null;
    if (l && h) return l === h ? `${l} ${unit}` : `${l}–${h} ${unit}`;
    if (l) return `${l}+ ${unit}`;
    if (h) return `Up to ${h} ${unit}`;
    return null;
}

export function asList(v) {
    if (Array.isArray(v)) return v.filter(isChosen);
    return isChosen(v) ? [String(v)] : [];
}

// Normalise only "codey" values — enum constants like CAPITAL_GROWTH or
// snake_case — into Title Case. Values already written for humans ("Pre-approved",
// "Block of Units", "Rentvestor") are left exactly as the buyer's payload had
// them, so we never turn nice labels into "Pre-Approved" / "Block Of Units".
export const titleize = (s) => {
    const str = String(s);
    const codey = str.includes('_') || (str === str.toUpperCase() && /[A-Z]/.test(str));
    if (!codey) return str;
    return str.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const PORTAL_CATEGORY_LABELS = {
    buy: 'For sale', sold: 'Sold', off_market: 'Off market',
    commercial: 'Commercial', off_the_plan: 'Off the plan',
    house_and_land: 'House & land', rent: 'Rent',
};

// Human-readable chips for what the buyer actually searched. Returns [] for a
// bare browse (only a sort was sent), so the caller can say "Browsed all
// listings" instead of an empty detail row.
export function describeSearch(pj = {}) {
    const chips = [];
    if (isChosen(pj.search)) chips.push(`“${String(pj.search).trim()}”`);
    const suburbs = asList(pj.suburbs);
    if (suburbs.length) chips.push(suburbs.join(', '));
    const price = rangeLabel(pj.min_price, pj.max_price);
    if (price) chips.push(price);
    const beds = countRange(pj.min_beds, pj.max_beds, 'bed');
    if (beds) chips.push(beds);
    if (isChosen(pj.min_baths)) chips.push(`${String(pj.min_baths).trim()}+ bath`);
    if (isChosen(pj.min_cars)) chips.push(`${String(pj.min_cars).trim()}+ car`);
    if (isChosen(pj.portal_category)) {
        chips.push(PORTAL_CATEGORY_LABELS[pj.portal_category] || titleize(pj.portal_category));
    }
    return chips;
}

// Human-readable chips for the preferences the buyer saved at this point in time
// (this is the snapshot they "updated their preferences to").
export function describePreferences(pj = {}) {
    const chips = [];
    const suburbs = asList(pj.suburbs);
    if (suburbs.length) chips.push(suburbs.join(', '));
    const price = rangeLabel(pj.price_min, pj.price_max);
    if (price) chips.push(price);
    const beds = countRange(pj.bedrooms_min, pj.bedrooms_max, 'bed');
    if (beds) chips.push(beds);
    if (isChosen(pj.bathrooms)) chips.push(`${String(pj.bathrooms).trim()}+ bath`);
    if (isChosen(pj.car_spaces)) chips.push(`${String(pj.car_spaces).trim()}+ car`);
    const land = rangeLabel(pj.land_size_min, pj.land_size_max);
    if (land) chips.push(`${land} land`);
    asList(pj.property_type).forEach(t => chips.push(titleize(t)));
    asList(pj.buyer_type).forEach(t => chips.push(titleize(t)));
    if (isChosen(pj.buying_timeline)) chips.push(String(pj.buying_timeline).trim());
    asList(pj.finance_status).forEach(t => chips.push(titleize(t)));
    asList(pj.investment_goals).forEach(t => chips.push(titleize(t)));
    return chips;
}
